import React, { useState } from "react";

interface Booking {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  deliveryMethod?: string;
}

interface MonthCalendarProps {
  bookings: Booking[];
  monthDates: Date[];
  eventColors: string[];
  currentDate: Date;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString("nb-NO", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const isSameDay = (d1: Date, d2: Date): boolean =>
  d1.getDate() === d2.getDate() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getFullYear() === d2.getFullYear();

const isCurrentMonth = (date: Date, current: Date): boolean =>
  date.getMonth() === current.getMonth() &&
  date.getFullYear() === current.getFullYear();

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  bookings = [],
  monthDates = [],
  eventColors = [
    "#1b4e8c",
    "#d32f2f",
    "#388e3c",
    "#f57c00",
    "#7b1fa2",
    "#0097a7",
    "#5d4037",
    "#616161",
  ],
  currentDate = new Date(),
}) => {
  const [showPopup, setShowPopup] = useState<string | null>(null);

  const getAllEventsForDay = (date: Date) => {
    const startingEvents: Booking[] = [];
    const continuingEvents: Booking[] = [];

    bookings.forEach((booking) => {
      const start = new Date(
        booking.start.getFullYear(),
        booking.start.getMonth(),
        booking.start.getDate()
      );
      const end = new Date(
        booking.end.getFullYear(),
        booking.end.getMonth(),
        booking.end.getDate()
      );
      const checkDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      if (checkDate >= start && checkDate <= end) {
        if (isSameDay(checkDate, start)) {
          startingEvents.push(booking);
        } else {
          continuingEvents.push(booking);
        }
      }
    });

    return { startingEvents, continuingEvents };
  };

  if (!Array.isArray(monthDates) || monthDates.length === 0) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          border: "2px solid red",
          backgroundColor: "#fff",
        }}
      >
        <h3>Kalender Problem</h3>
        <p>monthDates er tom eller ikke en array</p>
        <p>monthDates.length: {monthDates ? monthDates.length : "undefined"}</p>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", fontFamily: "system-ui, sans-serif" }}>
      {/* Kalender grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: "repeat(6, 400px)",
          gap: "1px",
          backgroundColor: "#ddd",
          position: "relative",
        }}
      >
        {monthDates.map((date, index) => {
          const dayKey = `day-${index}`;
          const { startingEvents, continuingEvents } = getAllEventsForDay(date);

          const allDayEvents = [...startingEvents, ...continuingEvents];
          const maxSingleEvents = 20;

          const visibleEvents = allDayEvents.slice(0, maxSingleEvents);
          const hiddenEvents = allDayEvents.slice(maxSingleEvents);

          return (
            <div
              key={index}
              style={{
                backgroundColor: isSameDay(date, new Date())
                  ? "#e8f5e8"
                  : !isCurrentMonth(date, currentDate)
                  ? "#f8f9fa"
                  : "white",
                border: isSameDay(date, new Date())
                  ? "2px solid #4caf50"
                  : "none",
                padding: "4px",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
              }}
              onClick={() => setShowPopup(showPopup === dayKey ? null : dayKey)}
            >
              {/* Dag nummer */}
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  marginBottom: "4px",
                  color: isSameDay(date, new Date()) ? "#2e7d32" : "#333",
                  height: "20px",
                }}
              >
                {date.getDate()}
              </div>

              {/* Single-day events */}
              <div
                style={{
                  maxHeight: "340px",
                  overflow: "hidden",
                }}
              >
                {visibleEvents.map((event, i) => {
                  const eventStartDate = new Date(
                    event.start.getFullYear(),
                    event.start.getMonth(),
                    event.start.getDate()
                  );
                  const todayDate = new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate()
                  );
                  const isStartingToday = isSameDay(eventStartDate, todayDate);

                  return (
                    <div
                      key={i}
                      style={{
                        backgroundColor:
                          eventColors[
                            bookings.indexOf(event) % eventColors.length
                          ],
                        color: "white",
                        fontSize: "0.6rem",
                        padding: "1px 3px",
                        marginBottom: "1px",
                        borderRadius: "2px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        border: isStartingToday ? "1px solid yellow" : "none",
                      }}
                      title={`${event.title} - ${
                        isStartingToday ? "STARTER I DAG" : "Fortsetter fra før"
                      }`}
                    >
                      {isStartingToday ? "🆕 " : ""}
                      {event.title}
                    </div>
                  );
                })}

                {/* Dropdown knapp */}
                {hiddenEvents.length > 0 && (
                  <button
                    style={{
                      backgroundColor: "#ff5722",
                      color: "white",
                      border: "none",
                      padding: "2px 4px",
                      borderRadius: "2px",
                      fontSize: "0.6rem",
                      cursor: "pointer",
                      fontWeight: "bold",
                      width: "100%",
                      height: "16px",
                      marginTop: "2px",
                    }}
                  >
                    +{hiddenEvents.length}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal popup */}
      {showPopup && (
        <div>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              zIndex: 998,
            }}
            onClick={() => setShowPopup(null)}
          />
          <div
            style={{
              position: "fixed",
              top: "10vh",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "white",
              border: "3px solid #ff5722",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
              zIndex: 999,
              width: "90vw",
              maxWidth: "400px",
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#ff5722",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "5px 5px 0 0",
                position: "relative",
              }}
            >
              Alle utleier for valgt dag
              <button
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "white",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
                onClick={() => setShowPopup(null)}
              >
                ×
              </button>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "8px",
              }}
            >
              {monthDates.map((date, dateIndex) => {
                const dayKey = `day-${dateIndex}`;
                if (showPopup !== dayKey) return null;

                const { startingEvents, continuingEvents } =
                  getAllEventsForDay(date);
                const allEvents = [...startingEvents, ...continuingEvents];

                return allEvents.map((event, eventIndex) => {
                  const eventStartDate = new Date(
                    event.start.getFullYear(),
                    event.start.getMonth(),
                    event.start.getDate()
                  );
                  const isStartingToday = isSameDay(eventStartDate, date);

                  return (
                    <div
                      key={eventIndex}
                      style={{
                        padding: "8px",
                        marginBottom: "4px",
                        backgroundColor: isStartingToday
                          ? "#e8f5e8"
                          : "#f5f5f5",
                        borderRadius: "4px",
                        border: isStartingToday
                          ? "2px solid #4caf50"
                          : "1px solid #ddd",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          marginBottom: "2px",
                        }}
                      >
                        {isStartingToday ? "🆕 " : ""}
                        {event.title}
                        {isStartingToday ? " (STARTER I DAG)" : " (fortsetter)"}
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#666",
                        }}
                      >
                        {formatDate(event.start)} – {formatDate(event.end)}
                      </div>
                    </div>
                  );
                });
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthCalendar;
